import { FC, useCallback, useState } from 'react';
import { Video, Ban, Check, X } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { useCan } from '@new/casl/hooks/useCan';
import {
  useAcceptScheduledCallMutation,
  useCancelScheduledCallMutation,
  useRejectScheduledCallMutation,
  type ScheduledCall,
} from '@teachur/http-client';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { Textarea } from '@new/common/ui/Textarea/Textarea';

interface ScheduledCallActionsProps {
  scheduledCall: ScheduledCall;
  onClose: () => void;
  onJoinCall?: (videoCallId: string) => void;
}

export const ScheduledCallActions: FC<ScheduledCallActionsProps> = ({
  scheduledCall,
  onClose,
  onJoinCall,
}) => {
  const canManageCourseHearing = useCan('manage', 'CourseHearing');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const { mutate: cancelCall, isPending: isCancelling } = useCancelScheduledCallMutation(
    scheduledCall.courseId,
    {
      onSuccess: () => {
        setCancelReason('');
        setIsCancelModalOpen(false);
        onClose();
      },
    }
  );
  const { mutate: acceptCall, isPending: isAccepting } = useAcceptScheduledCallMutation({
    onSuccess: onClose,
  });
  const { mutate: rejectCall, isPending: isRejecting } = useRejectScheduledCallMutation({
    onSuccess: onClose,
  });

  const handleAccept = useCallback(
    () => acceptCall(scheduledCall.id),
    [acceptCall, scheduledCall.id]
  );

  const handleReject = useCallback(
    () => rejectCall({ callId: scheduledCall.id, reason: 'Rejected by instructor' }),
    [rejectCall, scheduledCall.id]
  );

  const handleCancel = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(() => {
    if (cancelReason.trim()) {
      cancelCall({ callId: scheduledCall.id, reason: cancelReason.trim() });
    }
  }, [cancelCall, scheduledCall.id, cancelReason]);

  const handleCancelModalClose = useCallback(() => {
    if (!isCancelling) {
      setCancelReason('');
      setIsCancelModalOpen(false);
    }
  }, [isCancelling]);

  const handleJoinCall = useCallback(() => {
    if (scheduledCall.videoCallId && onJoinCall) {
      onJoinCall(scheduledCall.videoCallId);
    }
  }, [scheduledCall.videoCallId, onJoinCall]);

  const showJoinButton =
    scheduledCall.status === 'accepted' && scheduledCall.videoCallId && onJoinCall;
  const showPendingActions = canManageCourseHearing && scheduledCall.status === 'pending';
  const showCancelButton = canManageCourseHearing && scheduledCall.status === 'accepted';
  const showCloseButton = !canManageCourseHearing;

  return (
    <>
      <div className="mt-6 flex flex-col gap-3">
        {showJoinButton && (
          <Button onClick={handleJoinCall} variant="primary" className="w-full">
            <Video className="w-4 h-4 mr-2" />
            Join Call
          </Button>
        )}

        {showPendingActions && (
          <div className="flex gap-3">
            <Button
              onClick={handleAccept}
              variant="primary"
              disabled={isAccepting}
              className="flex-1 h-12!"
            >
              <Check className="w-4 h-4 mr-2" />
              {isAccepting ? 'Accepting...' : 'Accept'}
            </Button>
            <Button
              onClick={handleReject}
              variant="secondary"
              disabled={isRejecting}
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-2" />
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        )}

        {showCancelButton && (
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={isCancelling}
            className="w-full border-red-200 text-red-600 hover:bg-red-50 h-12! cursor-pointer"
          >
            <Ban className="w-4 h-4 mr-2" />
            {isCancelling ? 'Cancelling...' : 'Cancel Call'}
          </Button>
        )}

        {showCloseButton && (
          <Button onClick={onClose} variant="secondary" className="w-full">
            Close
          </Button>
        )}
      </div>

      <ConfirmationModal
        isOpen={isCancelModalOpen}
        title="Cancel scheduled call?"
        description="Are you sure you want to cancel this scheduled call? Please tell us why."
        confirmText="Yes"
        cancelText="No"
        isLoading={isCancelling}
        confirmDisabled={!cancelReason.trim()}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelConfirm}
      >
        <Textarea
          value={cancelReason}
          onChange={setCancelReason}
          placeholder="Reason for cancelling..."
          rows={3}
        />
      </ConfirmationModal>
    </>
  );
};
